import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AccountFeaturesEnum } from '@app/interfaces/config/accountFeaturesEnum';
import { FeatureFlagService } from '@app/services/feature-flag.service';
import { ModulePackagesService } from '@app/services/api/module-packages.service';
import { AuthService } from '@app/services/auth.service';

export interface VirtualSalonEntitlement {
  isProActive: boolean;
  isBasicActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class VirtualSalonEntitlementRefreshService {
  public readonly refresh$ = new Subject<void>();

  // Single shared resolution of "is this account entitled to Virtual Salon BASIC/PRO",
  // recomputed on every notifyChanged() and replayed to every subscriber - replaces the
  // identical nested featureFlags -> getActiveModulePackages lookup that used to be
  // hand-duplicated in VirtualSalonBannerComponent and VirtualSalonHeroComponent.
  public readonly entitlement$: Observable<VirtualSalonEntitlement> = this.refresh$.pipe(
    startWith(undefined),
    switchMap(() => this.resolveEntitlement()),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(
    private featureFlagService: FeatureFlagService,
    private modulePackagesService: ModulePackagesService,
    private authService: AuthService
  ) {
    // Mirrors ConfigAPIService/ApiCachingService: entitlement$ is a providedIn: 'root'
    // shareReplay cache that must be invalidated on account switch/auth change, otherwise
    // a multi-account user keeps seeing the previous account's entitlement.
    this.authService.getAccountControlChanges().subscribe(() => this.notifyChanged());
    this.authService.getAuthChanges().subscribe(() => this.notifyChanged());
    this.authService.getInvalidateConfigChanges().subscribe(() => this.notifyChanged());
  }

  notifyChanged(): void {
    this.refresh$.next();
  }

  private resolveEntitlement(): Observable<VirtualSalonEntitlement> {
    return this.featureFlagService.getFeatureFlags().pipe(
      switchMap((flags) => {
        const isProActive = !!flags[AccountFeaturesEnum.HAS_VIRTUAL_SALON_PRO];
        const basicFlag = !!flags[AccountFeaturesEnum.HAS_VIRTUAL_SALON_BASIC];

        if (!basicFlag) {
          return of({ isProActive, isBasicActive: false });
        }

        return this.modulePackagesService.getActiveModulePackages().pipe(
          map((result) => ({
            isProActive,
            isBasicActive: (result.data ?? []).some(
              (pkg) => !!pkg.featureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_BASIC]
            ),
          })),
          // Fail closed: a transient error resolving active module packages must not be
          // mistaken for entitlement (previously left isEntitled/isBasicActive stuck at
          // a stale/default value with no error handling at all).
          catchError(() => of({ isProActive, isBasicActive: false }))
        );
      }),
      catchError(() => of({ isProActive: false, isBasicActive: false }))
    );
  }
}
